import React, { useMemo, useState } from 'react'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { Option, Select } from '@/components/atoms/select'
import { Popup } from '@/components/atoms/popup'

const UNSELECTED_TIME_VALUE = '__unselected__'

type Props = {
  label: string
  valueFrom?: string
  valueTo?: string
  minTime?: string
  maxTime?: string
  intervalMinutes?: number
  disabled?: boolean
  width?: number
  onChange: (value: { valueFrom?: string; valueTo?: string }) => void
}

export const FixedLabelTimeRangeSelect: React.FC<Props> = ({
  label,
  valueFrom,
  valueTo,
  minTime,
  maxTime,
  intervalMinutes = 15,
  disabled = false,
  width = 116,
  onChange,
}) => {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)

  const timeOptions = useMemo(() => generateTimeOptions(minTime, maxTime, intervalMinutes), [intervalMinutes, maxTime, minTime])
  const isActive = !!valueFrom || !!valueTo
  const placeholder = t('RestaurantManager.Unselected')

  const handleChange = (key: 'valueFrom' | 'valueTo', nextValue?: string) => {
    const normalizedValue = !nextValue || nextValue === UNSELECTED_TIME_VALUE ? undefined : nextValue
    onChange({
      valueFrom: key === 'valueFrom' ? normalizedValue : valueFrom,
      valueTo: key === 'valueTo' ? normalizedValue : valueTo,
    })
  }

  return (
    <div css={wrapperStyle(width)} onClick={event => event.stopPropagation()}>
      <button
        type="button"
        css={triggerStyle({ isActive, isOpen, disabled })}
        disabled={disabled}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <span css={labelStyle(isActive, disabled)}>{label}</span>
        <img css={arrowStyle} src={require('@/static/images/arrow_down_yellow.svg')} alt="" />
      </button>

      <Popup isOpen={isOpen} onClose={() => setIsOpen(false)} customStyle={popupStyle}>
        <div css={rangeContainerStyle}>
          <Select
            value={valueFrom}
            allowClear
            placeholder={placeholder}
            customStyle={innerSelectStyle}
            getPopupContainer={trigger => trigger.parentElement as HTMLElement}
            dropdownStyle={{ width: 88 }}
            onChange={nextValue => handleChange('valueFrom', nextValue as string | undefined)}
          >
            <Option value={UNSELECTED_TIME_VALUE}>{t('RestaurantManager.Unselected')}</Option>
            {timeOptions.map(option => (
              <Option key={`from-${option}`} value={option}>
                {option}
              </Option>
            ))}
          </Select>

          <span css={separatorStyle}>~</span>

          <Select
            value={valueTo}
            allowClear
            placeholder={placeholder}
            customStyle={innerSelectStyle}
            getPopupContainer={trigger => trigger.parentElement as HTMLElement}
            dropdownStyle={{ width: 88 }}
            onChange={nextValue => handleChange('valueTo', nextValue as string | undefined)}
          >
            <Option value={UNSELECTED_TIME_VALUE}>{t('RestaurantManager.Unselected')}</Option>
            {timeOptions.map(option => (
              <Option key={`to-${option}`} value={option}>
                {option}
              </Option>
            ))}
          </Select>
        </div>
      </Popup>
    </div>
  )
}

const generateTimeOptions = (minTime?: string, maxTime?: string, intervalMinutes = 15): string[] => {
  if (!minTime || !maxTime) {
    return []
  }

  const min = convertTimeToMinutes(minTime)
  const max = convertTimeToMinutes(maxTime)

  if (Number.isNaN(min) || Number.isNaN(max) || min > max) {
    return []
  }

  const options: string[] = []
  for (let minute = min; minute <= max; minute += intervalMinutes) {
    options.push(convertMinutesToTime(minute))
  }

  if (options[options.length - 1] !== maxTime) {
    options.push(maxTime)
  }

  return Array.from(new Set(options))
}

const convertTimeToMinutes = (time: string): number => {
  const [hour, minute] = time.split(':').map(Number)
  return hour * 60 + minute
}

const convertMinutesToTime = (totalMinutes: number): string => {
  const hour = Math.floor(totalMinutes / 60)
  const minute = totalMinutes % 60
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
}

const wrapperStyle = (width: number) =>
  css({
    position: 'relative',
    width,
  })

const triggerStyle = ({ isActive, isOpen, disabled }: { isActive: boolean; isOpen: boolean; disabled: boolean }) =>
  css({
    width: '100%',
    height: 36,
    padding: '0 34px 0 16px',
    borderRadius: 18,
    border: `1px solid ${isActive || isOpen ? '#F8AA00' : '#CCCCCC'}`,
    backgroundColor: '#FFFFFF',
    display: 'flex',
    alignItems: 'center',
    textAlign: 'left',
    cursor: disabled ? 'default' : 'pointer',
  })

const labelStyle = (isActive: boolean, disabled: boolean) =>
  css({
    color: disabled ? '#BFBFBF' : isActive ? '#F8AA00' : '#676767',
    fontSize: 14,
    fontWeight: isActive ? 'bold' : 'normal',
    whiteSpace: 'nowrap',
  })

const arrowStyle = css({
  position: 'absolute',
  right: 14,
  top: '50%',
  transform: 'translateY(-50%)',
  width: 12,
  height: 7,
  pointerEvents: 'none',
})

const popupStyle = css({
  top: 40,
  left: 0,
})

const rangeContainerStyle = css({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: 12,
  backgroundColor: '#FFFFFF',
  borderRadius: 8,
})

const innerSelectStyle = css({
  paddingBottom: 0,
  width: 96,
  // '> div' にすると getPopupContainer で生成される rc-trigger のコンテナ div にも
  // minHeight が当たり、閉じた後もセレクトを覆ってクリックを遮ってしまう
  '> .ant-select': {
    borderRadius: 16,
    minHeight: 32,
  },
  '.ant-select-selection__rendered': {
    paddingLeft: '6px !important',
    marginRight: '22px !important',
  },
})

const separatorStyle = css({
  color: '#676767',
  fontSize: 16,
  lineHeight: 1,
  marginTop: -8,
})
